import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import dashboardService from '@/services/dashboard.service';

export const loadDashboard = createAsyncThunk(
  'dashboard/load',
  async (range = '30d', { rejectWithValue }) => {
    try {
      const [summary, revenue, signups, ingestion, planDist, examDist, categoryDist, recentUsers, recentPayments, recentNews] =
        await Promise.all([
          dashboardService.summary(),
          dashboardService.charts({ metric: 'revenue', range, groupBy: 'day' }),
          dashboardService.charts({ metric: 'signups', range, groupBy: 'day' }),
          dashboardService.charts({ metric: 'ingestion', range, groupBy: 'day' }),
          // 4-tier plans: split users by effective tier (free/pro/premium/elite), not the raw plan mirror.
          dashboardService.distributions({ dimension: 'tier' }),
          dashboardService.distributions({ dimension: 'exam' }),
          dashboardService.distributions({ dimension: 'category' }),
          dashboardService.recent({ entity: 'users', limit: 5 }),
          dashboardService.recent({ entity: 'payments', limit: 5 }),
          dashboardService.recent({ entity: 'news', limit: 5 }),
        ]);
      return {
        summary,
        charts: { revenue: revenue.series, signups: signups.series, ingestion: ingestion.series },
        distributions: { plan: planDist.buckets, exam: examDist.buckets, category: categoryDist.buckets },
        recent: { users: recentUsers.data, payments: recentPayments.data, news: recentNews.data },
      };
    } catch (err) {
      return rejectWithValue(err);
    }
  },
);

const dashboardSlice = createSlice({
  name: 'dashboard',
  initialState: {
    range: '30d',
    summary: null,
    charts: { revenue: [], signups: [], ingestion: [] },
    distributions: { plan: [], exam: [], category: [] },
    recent: { users: [], payments: [], news: [] },
    loading: false,
    error: null,
  },
  reducers: {
    setRange(state, { payload }) {
      state.range = payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(loadDashboard.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(loadDashboard.fulfilled, (state, { payload }) => {
        state.loading = false;
        Object.assign(state, payload);
      })
      .addCase(loadDashboard.rejected, (state, { payload, error }) => {
        state.loading = false;
        state.error = payload || { message: error.message };
      });
  },
});

export const { setRange } = dashboardSlice.actions;
export default dashboardSlice.reducer;
